import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Tv,
} from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { Link } from "react-router-dom";

import PageHeader from "../../components/PageHeader";
import { SELECT_STYLES } from "../../components/DateFilters/constants";
import { useAuth } from "../../context/AuthContext";
import {
  canAccessPath,
  canAlterarQuadroPainelBordeiro,
  canSelecionarLojaOperacional,
} from "../../lib/screenAccess";
import { lojaService } from "../../services/lojaService";
import { painelBordeiroService } from "../../services/painelBordeiroService";
import VisaoDiaria from "./components/VisaoDiaria";
import VisaoSemanal from "./components/VisaoSemanal";
import { addDiasISO, formatDataCompleta, formatDiaMes } from "./utils";

type Modo = "diaria" | "semanal";
type LojaOpt = { value: number; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;
const PERCENTUAL_PADRAO = 80;

const PainelBordeiro: React.FC = () => {
  const { user } = useAuth();
  const podeEscolherLoja = canSelecionarLojaOperacional(user);
  const podeAlterarQuadro = canAlterarQuadroPainelBordeiro(user);
  const podeVerTv = canAccessPath(user, "/ranking/painel-bordeiro");
  const [modo, setModo] = useState<Modo>("diaria");
  const [data, setData] = useState<string | null>(null);
  const [lojaId, setLojaId] = useState<number | null>(user?.lojaId ?? null);
  const [percentual, setPercentual] = useState<number | undefined>(undefined);
  const [percentualRascunho, setPercentualRascunho] = useState("");

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
    enabled: podeEscolherLoja,
  });
  const lojaOptions = useMemo(
    () => lojas.map((l) => ({ value: l.id, label: l.nome })),
    [lojas]
  );
  const lojaSel = lojaOptions.find((o) => o.value === lojaId) ?? null;

  const filtro = { data: data ?? undefined, lojaId, percentual };
  const diaQueryKey = ["painel-bordeiro", "dia", filtro];

  const dia = useQuery({
    queryKey: diaQueryKey,
    queryFn: () => painelBordeiroService.getDia(filtro),
    enabled: modo === "diaria",
    refetchInterval: (q) =>
      q.state.data?.producao?.status === "em_producao" ? 20_000 : false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const semana = useQuery({
    queryKey: ["painel-bordeiro", "semana", filtro],
    queryFn: () => painelBordeiroService.getSemana(filtro),
    enabled: modo === "semanal",
    staleTime: 0,
  });

  const hoje = dia.data?.hoje ?? semana.data?.hoje ?? null;
  const dataAtual = data ?? hoje;
  const percentualAtual =
    (modo === "diaria" ? dia.data?.percentual : semana.data?.percentual) ??
    percentual ??
    PERCENTUAL_PADRAO;

  useEffect(() => {
    setPercentualRascunho(String(percentualAtual));
  }, [percentualAtual]);

  const aplicarPercentual = () => {
    const n = Math.round(Number(percentualRascunho));
    if (!Number.isFinite(n) || n < 1 || n > 300) {
      setPercentualRascunho(String(percentualAtual));
      return;
    }
    setPercentual(n);
  };

  const navegar = (delta: number) => {
    if (!dataAtual) return;
    setData(addDiasISO(dataAtual, modo === "diaria" ? delta : delta * 7));
  };

  const carregando = modo === "diaria" ? dia.isFetching : semana.isFetching;
  const erro = modo === "diaria" ? dia.error : semana.error;
  const producaoTravaPercentual =
    modo === "diaria" && dia.data?.producao != null;

  const tituloPeriodo =
    modo === "diaria"
      ? dia.data
        ? `${dia.data.diaSemana}, ${formatDataCompleta(dia.data.data)}`
        : ""
      : semana.data
        ? `Semana ${formatDiaMes(semana.data.inicio)} a ${formatDiaMes(semana.data.fim)}`
        : "";

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Painel Bordeiro"
        description="Quantas bordas produzir em cada dia, com base no que foi vendido no mesmo dia da semana anterior."
      />

      <div className="mb-5 flex flex-col gap-4 rounded-xl border border-neutral-200 bg-neutral-50/80 p-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-wrap items-end gap-4">
          <div className="inline-flex rounded-lg border border-neutral-300 bg-white p-1">
            {(["diaria", "semanal"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setModo(m)}
                className={`rounded-md px-4 py-1.5 text-sm font-semibold ${
                  modo === m
                    ? "bg-primary text-white"
                    : "text-neutral-600 hover:bg-neutral-100"
                }`}
              >
                {m === "diaria" ? "Diária" : "Semanal"}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => navegar(-1)}
              disabled={!dataAtual}
              className="rounded-lg border border-neutral-300 bg-white p-2 hover:bg-neutral-100 disabled:opacity-40"
              aria-label="Anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <input
              type="date"
              value={dataAtual ?? ""}
              onChange={(e) => e.target.value && setData(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              type="button"
              onClick={() => navegar(1)}
              disabled={!dataAtual}
              className="rounded-lg border border-neutral-300 bg-white p-2 hover:bg-neutral-100 disabled:opacity-40"
              aria-label="Próximo"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setData(null)}
              className="ml-1 inline-flex items-center gap-1 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100"
            >
              <CalendarDays className="h-4 w-4" /> Hoje
            </button>
          </div>

          {podeAlterarQuadro && (
            <label className="flex flex-col gap-1 text-xs font-medium text-neutral-700">
              % da semana anterior
              <input
                type="number"
                min={1}
                max={300}
                value={percentualRascunho}
                disabled={producaoTravaPercentual}
                title={
                  producaoTravaPercentual
                    ? "A produção deste dia já foi confirmada. Use “Editar quadro” para ajustar."
                    : undefined
                }
                onChange={(e) => setPercentualRascunho(e.target.value)}
                onBlur={aplicarPercentual}
                onKeyDown={(e) => e.key === "Enter" && aplicarPercentual()}
                className="w-24 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-neutral-100"
              />
            </label>
          )}

          {podeEscolherLoja && (
            <div className="flex min-w-[200px] flex-col gap-1">
              <span className="text-xs font-medium text-neutral-700">Loja</span>
              <ReactSelect<LojaOpt, false>
                value={lojaSel}
                onChange={(o: SingleValue<LojaOpt>) =>
                  setLojaId(o?.value ?? user?.lojaId ?? null)
                }
                options={lojaOptions}
                styles={LOJA_SELECT_STYLES}
                className="text-sm"
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm font-semibold text-neutral-800">
            {tituloPeriodo}
          </p>
          {podeVerTv && (
            <Link
              to="/ranking/painel-bordeiro"
              className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100"
            >
              <Tv className="h-4 w-4" /> Modo TV
            </Link>
          )}
        </div>
      </div>

      <div className="relative min-h-[200px]">
        {carregando && !(dia.data || semana.data) && (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}
        {erro && (
          <p className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {(erro as Error).message}
          </p>
        )}
        {modo === "diaria" && dia.data && (
          <VisaoDiaria dados={dia.data} queryKey={diaQueryKey} />
        )}
        {modo === "semanal" && semana.data && (
          <VisaoSemanal
            dados={semana.data}
            onSelecionarDia={(d) => {
              setData(d);
              setModo("diaria");
            }}
          />
        )}
      </div>
    </main>
  );
};

export default PainelBordeiro;
